import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {UpdateArchivableParams} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {
  DilutionAssignmentType,
  ProcedureEdgeType,
  ProcedureParameterConfirmationMethod,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ToServerDocumentWithMetadata} from 'notebook/documents/DocumentConstants';
import {FloatSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import {ProcedureAnalysisTemplateMappings} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigTypes';
import {
  DataParameterTypedValue,
  EquipmentTypedModel,
  EquipmentTypedValue,
  MaterialInputAmountTypedValue,
  MaterialInputTypedValue,
  ProcedureStepGroupType,
} from 'procedures/ProcedureDomainTypes';
import {processFlowchartConfigURL} from 'processes/dao/ProcessFlowchartConfigDAO';
import {FieldType, RequiredLink} from 'schemas/fields/SchemaFieldConstants';
import {getJSONPromise, patchJSONPromise, postJSONPromise} from 'util/Net';

export interface DocumentVersionREST {
  id: string;
  jsonData?: ToServerDocumentWithMetadata;
}
export interface ProcedureStepDefinitionVersionREST {
  id: string | null;
  cid: string;
  name: string;
  documentVersion?: DocumentVersionREST;
  fromId?: string;
}

export interface DataParameterTypeREST {
  type: FieldType.TEXT | FieldType.FLOAT | FieldType.DATETIME;
  requiredLink: null;
  unitId: string | null;
}

export interface MaterialInputTypeREST {
  type: FieldType;
  requiredLink: RequiredLink | null;
  unitId: string | null;
}

export interface MaterialOutputTypeREST {
  type: FieldType.BIOENTITY_LINK;
  requiredLink: RequiredLink;
  unitId: string | null;
}

export interface EquipmentTypeREST {
  type: FieldType;
  requiredLink: RequiredLink | null;
  unitId: string | null;
}

interface ProcedureParameterVersionREST {
  id: string | null;
  cid: string;
  name: string;
  description: string;
  clonedFromId?: string | null;
}

export interface ProcedureDataParameterVersionREST extends ProcedureParameterVersionREST {
  dataType: DataParameterTypeREST;
  minimum: FloatSchemaFieldTypedValue | null;
  maximum: FloatSchemaFieldTypedValue | null;
  procedureDataParameterId: string | null;
  confirmationMethod: ProcedureParameterConfirmationMethod;
}

export interface ProcedureMaterialInputVersionREST extends ProcedureParameterVersionREST {
  dataType: MaterialInputTypeREST;
  amountUnitId: string | null;
  procedureMaterialInputId: string | null;
  confirmationMethod: ProcedureParameterConfirmationMethod;
}

export interface ProcedureMaterialOutputVersionREST extends ProcedureParameterVersionREST {
  dataType: MaterialOutputTypeREST;
  procedureMaterialOutputId: string | null;
}

export interface ProcedureEquipmentVersionREST extends ProcedureParameterVersionREST {
  dataType: EquipmentTypeREST;
  allowedEquipmentModels: EquipmentTypedModel;
  procedureEquipmentId: string | null;
}

export interface ProcedureDataParameterPlannedValueREST {
  id: string | null;
  cid: string;
  value: DataParameterTypedValue;
}

export interface ProcedureMaterialInputPlannedValueREST {
  id: string | null;
  cid: string;
  material: MaterialInputTypedValue;
  amount: MaterialInputAmountTypedValue | null;
}

export interface ProcedureEquipmentPlannedValueREST {
  id: string | null;
  cid: string;
  equipment: EquipmentTypedValue;
}

export interface ProcedureDataParameterSettingREST {
  dataParameterVersion: ProcedureDataParameterVersionREST;
  values: Array<ProcedureDataParameterPlannedValueREST>;
}

export interface ProcedureMaterialInputSettingREST {
  materialInputVersion: ProcedureMaterialInputVersionREST;
  values: Array<ProcedureMaterialInputPlannedValueREST>;
}

export interface ProcedureMaterialOutputSettingREST {
  materialOutputVersion: ProcedureMaterialOutputVersionREST;
}

export interface ProcedureEquipmentSettingREST {
  equipmentVersion: ProcedureEquipmentVersionREST;
  values: Array<ProcedureEquipmentPlannedValueREST>;
}

export interface ProcedureStepGroupREST {
  group: ProcedureStepGroupType;
  steps: Array<ProcedureStepDefinitionVersionREST>;
}
export interface ProcedureAnalysisTemplateConfigurationREST {
  analysisTemplateId: string;
  configJson: ProcedureAnalysisTemplateMappings;
}
export interface ProcedureTestDefinitionREST {
  id: string;
}

export interface MethodNodeREST {
  id: string | null;
  cid: string;
  name: string;
  description: string;
  // Added for Procedure on Workflows -- will be null for non-PoW procedures and will be used to create
  // PMDVs in the backend procedure method node adapter.
  // See benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
  processFlowchartConfigId: string | null;
  stepGroups: Array<ProcedureStepGroupREST>;
  dataParameterSettings: Array<ProcedureDataParameterSettingREST>;
  materialInputSettings: Array<ProcedureMaterialInputSettingREST>;
  materialOutputSettings: Array<ProcedureMaterialOutputSettingREST>;
  equipmentSettings: Array<ProcedureEquipmentSettingREST>;
  sourceMethodId: string | null;
  nodeId: string;
  provenanceId: string;
  gridLayoutCoordinates: {row: number; column: number};
  analysisTemplateConfigurations: Array<ProcedureAnalysisTemplateConfigurationREST>;
  testDefinitions: Array<ProcedureTestDefinitionREST>;
  dilutionPlan: {allowedDilutionFactors: Array<number>; dilutionAssignment: DilutionAssignmentType};
}

export interface ProcedureEdgeREST {
  type: ProcedureEdgeType;
  sourceId: string; // Method id if method edge, material output id if material edge, etc
  targetId: string; // ditto
}

export interface ProcedureFlowchartREST {
  nodes: Array<MethodNodeREST>;
  edges: Array<ProcedureEdgeREST>;
  latestFlowchartVersionId?: string;
}

// Keep in sync with benchling/procedures/network/rest/procedure_rest_specs.py
export interface CreateProcedureParams {
  name: string;
  templateCollectionId: string;
  procedureType: ProcedureType;
  flowchart: ProcedureFlowchartREST;
  duplicatingProcedureId?: string;
}

export interface CreateProcedureResponse {
  createdIds: Array<string>;
}

export function createProcedure(procedure: CreateProcedureParams): Promise<CreateProcedureResponse> {
  const creates = [procedure];
  return postJSONPromise({url: getApiBaseForObjectType(DomainGraphType.PROCEDURE), data: {creates}});
}

// Keep in sync with benchling/procedures/network/rest/procedure_rest_specs.py
export interface UpdateProcedureParams extends Pick<UpdateArchivableParams, 'isArchived' | 'archiveReason'> {
  id: string;
  name?: string;
  flowchart?: ProcedureFlowchartREST;
  templateCollectionId?: string;
}

export interface UpdateProcedureResponse {
  updatedIds: Array<string>;
  cidToIdMap: Record<string, string>;
}

export function updateProcedure(procedure: UpdateProcedureParams): Promise<UpdateProcedureResponse> {
  const updates = [procedure];
  return patchJSONPromise({url: getApiBaseForObjectType(DomainGraphType.PROCEDURE), data: {updates}});
}

type GetProcedureVersionsParams =
  | {
      // Legacy
      flowchartConfigId?: never;
      procedureApiId: string;
      openFlowchartVersionId: string;
    }
  | {
      // PoW
      flowchartConfigId: string;
      procedureApiId?: never;
      openFlowchartVersionId: string;
    };

export async function getProcedureVersions(params: GetProcedureVersionsParams): Promise<Array<Version>> {
  const baseUrl = params.flowchartConfigId
    ? processFlowchartConfigURL()
    : getApiBaseForObjectType(DomainGraphType.PROCEDURE);
  const response = await getJSONPromise(
    `${baseUrl}/${params.flowchartConfigId ?? params.procedureApiId}/versions/${params.openFlowchartVersionId}`
  );
  return response.data;
}
export async function publishProcedureVersion(
  procedureApiId: string,
  versionApiId: string,
  versionName: string,
  versionDescription?: string
): Promise<{publishingRecordId: string}> {
  const response = await postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.PROCEDURE)}:publish`,
    data: {procedureId: procedureApiId, flowchartVersionId: versionApiId, versionName, versionDescription},
  });
  return response.data;
}

export async function withdrawProcedureVersion(
  procedureApiId: string,
  versionApiId: string
): Promise<{publishingRecordId: string}> {
  const response = await postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.PROCEDURE)}:withdraw`,
    data: {procedureId: procedureApiId, flowchartVersionId: versionApiId},
  });
  return response.data;
}

interface NewPublishedVersionData {
  versionId: string;
}

// A "draft" version is part of the publishing system: the user has a published version of this
// procedure, and now want to iterate on it while the published version remains usable for execution.
export function createNewDraftProcedureVersion(
  procedureApiId: string,
  versionToDuplicateApiId: string
): Promise<NewPublishedVersionData> {
  return postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.PROCEDURE)}:create_draft`,
    data: {procedureId: procedureApiId, flowchartVersionId: versionToDuplicateApiId},
  });
}
